import styles from './AddAssessmentNoAuth.module.scss';
import blueLogo from '../../../assets/logo_grad.svg';
import { Text } from '../../../components/typography';
import { useReactiveVar } from '@apollo/client';
import { showToast, toastVar, hideToast } from '../../../graphql/cache/modal';
import { useApolloContext } from '../../../graphql/ApiProvider';
import { useQueryParams } from '../../../hooks/misc';
import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { loader } from 'graphql.macro';
import { Button } from '../../../components';
import { Form, ProgressBar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  keys,
  getTranslationKey,
} from '../../../utilities/translator/translation_keys';
import Accordian from '../../../components/Accordian';
import QuestionNoAuth from './QuestionNoAuth';
import ConditionalInspectionItemNoAuth from './ConditionalInspectionItemNoAuth';
import Toast from '../../../components/modals/Toast';
import { toastVariant } from '../../../constants/misc';
import Spacer from '../../../components/typography/Spacer';
import moment from 'moment';
import Icon from '../../../components/Icon';
import { truncate } from '../../../utilities';
import { evaluateCondition } from '../../../utilities/inspection';
import { actionsKeys } from '../../../components/question_menu/conditionalActions/conditionalAction.utils';
import noImage from '../../../assets/icons/image_blank.png';

const startAssessmentMutation = loader('./AddAssessmentNoAuth.start.graphql');
const updateAssessmentMutation = loader('./AddAssessmentNoAuth.update.graphql');
const publishAssessmentMutation = loader(
  './AddAssessmentNoAuth.publish.graphql',
);
const fetchTemplateQuery = loader('./AddAssessmentNoAuth.fetch.graphql');
const fetchLatestTemplateQuery = loader(
  './AddAssessmentNoAuth.fetchLatest.graphql',
);
const fetchCompanyQuery = loader('./AddAssessmentNoAuth.company.graphql');
const submitAnswerMutation = loader('./QuestionNoAuth.answer.graphql');

const checkCategoryFailure = (category, itemMap) =>
  (category.questions ?? []).some((question) => !!itemMap[question.id]?.failed);

const CompleteView = ({
  onReset,
  companyLogo,
  assessmentName = 'Assessment',
}) => (
  <div className={styles.submission}>
    <div className={styles.submissionContent}>
      <img
        src={companyLogo ?? blueLogo}
        alt="OPAS Mobile Logo"
        className={styles.contentLogo}
      />
      <Text weight="semiBold" size="lg" textAlign="center" noMargin>
        {`Thank you for your ${assessmentName}!`}
      </Text>
      <Text weight="semiBold" textAlign="center">
        Every detail helps us build a safer future.
      </Text>
      <Button
        value={`Complete Another ${assessmentName}`}
        icon="person_search"
        align="center"
        onClick={onReset}
      />
    </div>
  </div>
);

export default function AddAssessmentNoAuth() {
  const toastProps = useReactiveVar(toastVar);
  const { t } = useTranslation();
  const completeKey = t(keys.common.COMPLETE);
  const { accessKey, setAccessKey } = useApolloContext();
  const { getParam } = useQueryParams();
  const key = getParam('accessKey');
  const workspaceId = getParam('workspaceId');
  const templateId = getParam('templateId');

  const [localAnswers, setLocalAnswers] = useState({});
  const [localSubject, setLocalSubject] = useState('');
  const [localLocation, setLocalLocation] = useState('');
  const [companyInfo, setCompanyInfo] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [editing, setEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(0);

  const [startAssessment] = useMutation(startAssessmentMutation);
  const [updateAssessment] = useMutation(updateAssessmentMutation);
  const [publishAssessment] = useMutation(publishAssessmentMutation);
  const [submitAnswer] = useMutation(submitAnswerMutation);

  useEffect(
    () => (key ? setAccessKey(key) : setAccessKey('')),
    [key, setAccessKey],
  );

  const {
    loading: loadingTemplate,
    data: { inspectionTemplates: [fetchedTemplate] = [{}] } = {},
  } = useQuery(fetchTemplateQuery, {
    skip: !key && !templateId && !accessKey,
    variables: { templateId },
  });

  const rootId = fetchedTemplate?.isArchived
    ? String(fetchedTemplate?.parentId ?? fetchedTemplate?.id)
    : null;

  const {
    loading: loadingLatest,
    data: { inspectionTemplates: [latestTemplate] = [] } = {},
  } = useQuery(fetchLatestTemplateQuery, {
    skip: !rootId || !accessKey,
    variables: { parentId: rootId },
  });

  const template = fetchedTemplate?.isArchived
    ? latestTemplate ?? fetchedTemplate
    : fetchedTemplate;

  useQuery(fetchCompanyQuery, {
    skip: !workspaceId || !accessKey,
    variables: { workspaceId },
    onCompleted: (data) => {
      const company = data?.workspaces?.[0]?.company;
      if (company) setCompanyInfo(company);
    },
  });

  // itemMap built from local answers for progress/validation tracking
  const itemMap = useMemo(() => localAnswers, [localAnswers]);

  const calculateRequiredProgress = (category, itemMap) => {
    const questions = category.questions ?? [];
    let completed = 0;
    let total = questions.filter((q) => q.required).length;
    questions.forEach((question) => {
      if (
        !!itemMap[question.id] &&
        !!itemMap[question.id]?.value &&
        question.required
      ) {
        completed += 1;
      }
      if (Array.isArray(question?.conditionalAction)) {
        const matchingActions = question.conditionalAction
          .filter((ca) =>
            evaluateCondition(itemMap[question?.id]?.value, {
              inputType: question?.inputType,
              conditionalAction: ca,
            }),
          )
          .flatMap((ca) => ca.actions ?? []);
        for (const { actionable, action } of matchingActions) {
          if (action === actionsKeys.followUpQuestion && actionable?.required) {
            total += 1;
            if (!!itemMap[actionable?.id]?.value) {
              completed += 1;
            }
          }
        }
      }
    });
    return completed === total;
  };

  const calculateCategoryProgress = (category, itemMap) => {
    const questions = category.questions ?? [];
    let completed = 0;
    let total = questions.length;
    questions.forEach((question) => {
      if (!!itemMap[question.id]?.value) {
        completed += 1;
      }
      if (Array.isArray(question?.conditionalAction)) {
        const matchingActions = question.conditionalAction
          .filter((ca) =>
            evaluateCondition(itemMap[question?.id]?.value, {
              inputType: question?.inputType,
              conditionalAction: ca,
            }),
          )
          .flatMap((ca) => ca.actions ?? []);
        for (const { actionable, action } of matchingActions) {
          if (action === actionsKeys.followUpQuestion) {
            total += 1;
            if (!!itemMap[actionable?.id]?.value) {
              completed += 1;
            }
          }
        }
      }
    });
    return completed === total
      ? completeKey
      : `${
          !calculateRequiredProgress(category, itemMap) ? '* ' : ''
        }${completed} / ${total}`;
  };

  const submitDisabled = useMemo(() => {
    return !template?.categories?.every((category) =>
      calculateRequiredProgress(category, itemMap),
    );
  }, [template?.categories, itemMap]);

  const handleLocalAnswer = (questionId, answerData) => {
    setLocalAnswers((prev) => ({ ...prev, [questionId]: answerData }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitProgress(0);

    const answerEntries = Object.entries(localAnswers);
    const totalSteps = 1 + answerEntries.length + (localLocation ? 1 : 0) + 1;
    let completed = 0;
    const advance = () =>
      setSubmitProgress(Math.round((++completed / totalSteps) * 100));

    try {
      const { data } = await startAssessment({
        variables: {
          templateId: parseInt(template?.id ?? templateId),
          inspectionType: template?.templateType,
          workspaceId: parseInt(workspaceId),
          subject: localSubject || undefined,
        },
      });
      advance();

      const inspectionId = data?.addInspection?.id;
      if (data?.addInspection?.company) {
        setCompanyInfo(data?.addInspection?.company);
      }

      await Promise.all(
        answerEntries.map(([questionId, answerData]) =>
          submitAnswer({
            variables: {
              inspectionId,
              questionId: parseInt(questionId),
              ...answerData,
            },
          }).then(advance),
        ),
      );

      if (localLocation) {
        await updateAssessment({
          variables: { id: inspectionId, location: localLocation },
        });
        advance();
      }

      await publishAssessment({
        variables: {
          id: inspectionId,
          isDraft: template?.reviewRequired,
          readyForReview: template?.reviewRequired,
        },
      });
      advance();

      setSubmitted(true);
    } catch (e) {
      showToast({
        title: 'Error',
        variant: toastVariant?.warning,
        message: `There was a problem saving your ${
          companyInfo?.assessmentName ?? 'assessment'
        }, contact an Admin for help.`,
      });
    } finally {
      setIsSubmitting(false);
      setSubmitProgress(0);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setCompanyInfo(null);
    setLocalAnswers({});
    setLocalSubject('');
    setLocalLocation('');
  };

  const today = new moment();

  return (
    <div className={styles.container}>
      <div className={styles.toastContainer}>
        <Toast {...toastProps} onHide={() => hideToast()} />
      </div>
      <div className={styles.background}>
        <div className={styles.backgroundGraphic} />
      </div>
      <div className={styles.content}>
        {submitted ? (
          <CompleteView
            onReset={handleReset}
            companyLogo={companyInfo?.logoUrl}
            assessmentName={
              !!companyInfo?.assessmentName
                ? companyInfo?.assessmentName
                : t(keys.common.ASSESSMENT)
            }
          />
        ) : (
          <>
            <div className={styles.contentInner}>
              <div className={styles.formHeader}>
                <div className={styles.spaceBetween}>
                  <div className={styles.top}>
                    <img className={styles.logo} src={blueLogo} alt="logo" />
                    <div>
                      <Text noMargin weight="bold" size="lg">
                        {loadingTemplate || loadingLatest
                          ? 'Loading...'
                          : `${template?.title ?? 'No Title'}`}
                      </Text>
                      {template?.description && (
                        <Text noMargin>{template.description}</Text>
                      )}
                    </div>
                  </div>
                </div>
                <Spacer />
                <div className={styles.subject}>
                  <div className={styles.spaceBetween}>
                    <Text weight="bold" size="md-lg" marginSm>
                      Document Subject
                    </Text>
                    <Text textAlign="right">{today.toString()}</Text>
                  </div>
                  <div className={styles.flexRow}>
                    {editing === 'subject' ? (
                      <div className={styles.subjectInput}>
                        <Form.Control
                          value={localSubject}
                          placeholder="Enter Document Subject"
                          onChange={(e) => setLocalSubject(e.target.value)}
                          onBlur={() => setEditing(null)}
                        />
                      </div>
                    ) : localSubject ? (
                      <Text weight="semiBold" noMargin>
                        {localSubject}
                      </Text>
                    ) : (
                      <Text weight="semiBold" noMargin color="secondary">
                        No Subject
                      </Text>
                    )}
                    {editing !== 'subject' ? (
                      <Icon
                        onClick={() =>
                          setEditing((prev) =>
                            prev === 'subject' ? null : 'subject',
                          )
                        }
                      >
                        edit
                      </Icon>
                    ) : (
                      <>
                        <Icon onClick={() => setEditing(null)}>check</Icon>
                        <Icon
                          onClick={() => {
                            setLocalSubject('');
                            setEditing(null);
                          }}
                        >
                          close
                        </Icon>
                      </>
                    )}
                  </div>
                </div>
                {template?.templateOptions?.location && (
                  <div className={styles.subject}>
                    <div className={styles.spacerXs} />
                    <div className={styles.spaceBetween}>
                      <Text weight="bold" size="md-lg" marginSm>
                        Location
                      </Text>
                    </div>
                    <div className={styles.flexRow}>
                      {editing === 'location' ? (
                        <div className={styles.subjectInput}>
                          <Form.Control
                            value={localLocation}
                            placeholder="Enter Location"
                            onChange={(e) => setLocalLocation(e.target.value)}
                            onBlur={() => setEditing(null)}
                          />
                        </div>
                      ) : localLocation ? (
                        <Text weight="semiBold" noMargin>
                          {localLocation}
                        </Text>
                      ) : (
                        <Text weight="semiBold" noMargin color="secondary">
                          No Location
                        </Text>
                      )}
                      {editing !== 'location' ? (
                        <Icon
                          onClick={() =>
                            setEditing((prev) =>
                              prev === 'location' ? null : 'location',
                            )
                          }
                        >
                          edit
                        </Icon>
                      ) : (
                        <>
                          <Icon onClick={() => setEditing(null)}>check</Icon>
                          <Icon
                            onClick={() => {
                              setLocalLocation('');
                              setEditing(null);
                            }}
                          >
                            close
                          </Icon>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {template?.vehicles?.length > 0 && (
                  <div>
                    {template?.vehicles?.map((vehicle) => {
                      const {
                        year,
                        make,
                        model,
                        type,
                        subtype,
                        imageUrl,
                        unitNumber,
                      } = vehicle;
                      return (
                        <div className={styles.vehicleContainer}>
                          <img
                            className={imageUrl ? styles.image : styles.noImage}
                            src={imageUrl ? imageUrl : noImage}
                            alt={`${make} ${model}`}
                          />
                          <div>
                            <div className={styles.vehicleHeader}>
                              <Text
                                size="md"
                                weight="bold"
                                className={styles.title}
                                noMargin
                              >
                                {truncate(
                                  `${year || ''} ${make || ''} ${model}`,
                                  25,
                                )}
                              </Text>
                              <Text
                                noMargin
                                size="md"
                                weight="semiBold"
                                color="secondary"
                              >
                                {truncate(unitNumber, 25)}
                              </Text>
                            </div>
                            <div className={styles.vehicleSubheader}>
                              <Text size="xs" color="secondary" noMargin>
                                {`${
                                  t(getTranslationKey(type, 'assets')) || type
                                } - ${
                                  t(getTranslationKey(subtype, 'assets')) ||
                                  subtype
                                }` || '-'}
                              </Text>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <Spacer />
                {template?.categories?.map((cat) => (
                  <div key={cat?.id} className={styles.marginBottomSm}>
                    <Accordian
                      boxShadow={false}
                      expand
                      size="lg"
                      isBundle={cat?.isBundle}
                      icon={cat.isBundle ? 'layers' : null}
                      iconProps={{
                        color: '#0d6efd',
                        outlined: true,
                        position: 'right',
                      }}
                      title={
                        !!template.internalType
                          ? t(getTranslationKey(cat.title, 'assessments'))
                          : cat.title
                      }
                      messageVariant={
                        checkCategoryFailure(cat, itemMap) ? 'red' : 'primary'
                      }
                      message={calculateCategoryProgress(cat, itemMap)}
                    >
                      {cat?.questions?.map((que, index) => (
                        <div key={`${cat.id}-${que?.id}=${index}`}>
                          <QuestionNoAuth
                            index={index}
                            inspectionId={null}
                            question={que}
                            onLocalAnswer={handleLocalAnswer}
                            company={companyInfo}
                          />
                          {Array.isArray(que?.conditionalAction) &&
                            que.conditionalAction.length > 0 && (
                              <ConditionalInspectionItemNoAuth
                                question={que}
                                localAnswers={localAnswers}
                                onLocalAnswer={handleLocalAnswer}
                                company={companyInfo}
                              />
                            )}
                        </div>
                      ))}
                    </Accordian>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.footer}>
              {isSubmitting && (
                <ProgressBar
                  now={submitProgress}
                  animated
                  className={styles.progressBar}
                  variant="success"
                />
              )}
              <Button
                size="md"
                className={styles.submitButton}
                variant="success"
                icon={isSubmitting ? undefined : 'check_circle'}
                value={isSubmitting ? 'Submitting...' : 'Submit'}
                align="center"
                disabled={submitDisabled || isSubmitting}
                onClick={handleSubmit}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
